import i18n from '../locales/i18n';

const APP_DATE_LOCALE = 'en-GB';
const DEFAULT_GALLERY_DATE_LOCALE = 'en';
const LONG_DATE_OPTIONS = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};
const HISTORY_TIME_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function currentGalleryDateLocale() {
  return i18n.resolvedLanguage || i18n.language || DEFAULT_GALLERY_DATE_LOCALE;
}

function getHistoryDateParts(date, locale = currentGalleryDateLocale()) {
  const formatter = new Intl.DateTimeFormat(locale, LONG_DATE_OPTIONS);
  const parts = formatter.formatToParts(date);

  return {
    day: parts.find((part) => part.type === 'day')?.value || '',
    month: parts.find((part) => part.type === 'month')?.value || '',
    year: parts.find((part) => part.type === 'year')?.value || '',
  };
}

function formatLongDate(date, locale) {
  const formatter = new Intl.DateTimeFormat(locale, LONG_DATE_OPTIONS);

  // Intl supplies the Russian word order and inflected month, but also emits
  // a trailing year marker ("г."). Product copy uses the same Intl parts with
  // a comma before the year and without that marker: "13 марта, 2026".
  if (locale.toLowerCase().startsWith('ru')) {
    const parts = formatter.formatToParts(date);
    const yearIndex = parts.findIndex(({ type }) => type === 'year');
    if (yearIndex > 0) {
      return parts
        .slice(0, yearIndex + 1)
        .map((part, index) => (
          index === yearIndex - 1 && part.type === 'literal'
            ? ', '
            : part.value
        ))
        .join('');
    }
  }

  return formatter.format(date);
}

export function formatAppDate(value) {
  const date = parseDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat(APP_DATE_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatGalleryDate(value) {
  const date = parseDate(value);
  if (!date) return '';

  return formatLongDate(date, currentGalleryDateLocale());
}

export function formatHistoryDate(value) {
  const date = parseDate(value);
  if (!date) return '';

  const { day, month, year } = getHistoryDateParts(date);
  return [day, month, year].filter(Boolean).join(' ');
}

export function formatAppDateTime(value) {
  const date = parseDate(value);
  if (!date) return '';

  const formattedDate = formatAppDate(date);
  const formattedTime = new Intl.DateTimeFormat(APP_DATE_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);

  return `${formattedDate}, ${formattedTime}`;
}

export function formatHistoryDateTime(value) {
  const date = parseDate(value);
  if (!date) return '';

  const formattedDate = formatHistoryDate(date);
  const formattedTime = new Intl.DateTimeFormat(
    currentGalleryDateLocale(),
    HISTORY_TIME_OPTIONS,
  ).format(date);

  return `${formattedDate}, ${formattedTime}`;
}
